import React, { useState } from 'react';
import { GlassPanel } from '../components/common/GlassPanel';
import { CONTACT_EMAIL } from '../data/config';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactImpl = ({ accent, mood }) => {
  const [fields, setFields] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  const validate = () => {
    const next = {};
    if (!fields.name.trim()) next.name = 'Please enter your name.';
    if (!fields.email.trim()) next.email = 'Please enter your email.';
    else if (!EMAIL_RE.test(fields.email.trim())) next.email = 'Please enter a valid email address.';
    if (!fields.message.trim()) next.message = 'Please enter a message.';
    return next;
  };

  const handleChange = (field) => (e) => {
    setFields((f) => ({ ...f, [field]: e.target.value }));
    setErrors((err) => ({ ...err, [field]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setStatus('submitting');
    const subject = encodeURIComponent(`Portfolio contact from ${fields.name}`);
    const body = encodeURIComponent(`${fields.message}\n\n- ${fields.name} (${fields.email})`);
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;

    setStatus('sent');
    setFields({ name: '', email: '', message: '' });
  };

  const inputClass = (field) =>
    `w-full bg-white/5 p-4 rounded-xl outline-none text-white focus-visible:ring-2 ${
      errors[field] ? 'ring-2 ring-red-500' : 'focus-visible:ring-white/30'
    }`;

  return (
    <div className="min-h-[100svh] pt-32 pb-64 px-4 sm:px-8 lg:px-24">
      <h2 className="text-5xl font-black italic text-white mb-16">CONTACT</h2>

      <GlassPanel mood={mood} className="p-12 rounded-3xl max-w-xl">
        <p className="text-white/60 mb-8">
          Want to collaborate or discuss opportunities?
        </p>

        <form className="space-y-6" onSubmit={handleSubmit} noValidate>
          <div>
            <label htmlFor="contact-name" className="block text-xs uppercase tracking-widest text-white/40 mb-2">Your Name</label>
            <input
              id="contact-name"
              name="name"
              type="text"
              autoComplete="name"
              required
              aria-required="true"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'contact-name-error' : undefined}
              value={fields.name}
              onChange={handleChange('name')}
              className={inputClass('name')}
            />
            {errors.name && <p id="contact-name-error" role="alert" className="mt-2 text-sm text-red-400">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="contact-email" className="block text-xs uppercase tracking-widest text-white/40 mb-2">Email</label>
            <input
              id="contact-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              aria-required="true"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'contact-email-error' : undefined}
              value={fields.email}
              onChange={handleChange('email')}
              className={inputClass('email')}
            />
            {errors.email && <p id="contact-email-error" role="alert" className="mt-2 text-sm text-red-400">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="contact-message" className="block text-xs uppercase tracking-widest text-white/40 mb-2">Message</label>
            <textarea
              id="contact-message"
              name="message"
              rows={4}
              required
              aria-required="true"
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? 'contact-message-error' : undefined}
              value={fields.message}
              onChange={handleChange('message')}
              className={inputClass('message')}
            />
            {errors.message && <p id="contact-message-error" role="alert" className="mt-2 text-sm text-red-400">{errors.message}</p>}
          </div>

          <button
            type="submit"
            disabled={status === 'submitting'}
            style={{ backgroundColor: accent.color }}
            className="px-8 py-3 font-black text-black rounded-xl disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {status === 'submitting' ? 'Opening…' : 'Send Message'}
          </button>

          <p role="status" aria-live="polite" className="text-sm text-white/50">
            {status === 'sent' && "Opening your email client with this message ready to send…"}
          </p>
        </form>
      </GlassPanel>
    </div>
  );
};

export const Contact = React.memo(ContactImpl);
